import { useMemo } from 'react';
import type { ReactElement } from 'react';
import { Flex } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useBasket } from '../../../../bff-api/baskets/useBasket';
import { usePaymentMethods } from '../../../../bff-api/baskets/usePaymentMethods';
import { useProduct } from '../../../../bff-api/products/useProduct';
import { AfterpaySection } from '../../../../components/afterpay-section';
import { BasketSummary } from '../../../../components/basket-summary/BasketSummary';
import { ClientOnly } from '../../../../components/client-only/ClientOnly';
import { ShippingProtectionToggle } from '../../../../components/shipping-protection/ShippingProtectionToggle';
import { TermsConditionsPrivacyPolicy } from '../../../../components/terms-conditions-privacy-policy';
import { SHIPPING_PROTECTION_PRODUCT_ID } from '../../../../constants';
import { useShippingProtectionConfigs } from '../../../../hooks/useShippingProtectionConfigs';
import { PageSource } from '../../../../types/page-source';
import { Header } from '../../../../ui-components';
import {
    getProductItemsCounts,
    shouldShowShippingProtection,
} from '../../../../utils/product-utils';
import { CartButtons } from '../cart-buttons';
import { CartPerks } from '../cart-perks';

export const CartOrderSummary = (): ReactElement => {
    const {
        data: paymentMethods,
        isError: isPaymentMethodsError,
        error: paymentMethodsError,
    } = usePaymentMethods();

    const { data: shippingProtectionProduct, isLoading: isShippingProtectionLoading } = useProduct(
        SHIPPING_PROTECTION_PRODUCT_ID,
    );

    const { isUIVisible } = useShippingProtectionConfigs();

    const { data: basket } = useBasket();

    const { countShippingProtectionEligible } = getProductItemsCounts(basket?.productItems || []);

    const showShippingProtection = useMemo(
        () =>
            shouldShowShippingProtection(
                countShippingProtectionEligible,
                shippingProtectionProduct,
                isUIVisible,
                basket?.c_shippingProtectionFee,
            ),
        [
            countShippingProtectionEligible,
            shippingProtectionProduct,
            isUIVisible,
            basket?.c_shippingProtectionFee,
        ],
    );

    const hasShippingProtectionInCart = basket?.productItems?.some(
        (item) => item.productId === shippingProtectionProduct?.id,
    );

    if (isPaymentMethodsError) {
        throw paymentMethodsError;
    }

    return (
        <Flex data-testid="cart-order-summary" direction="column">
            <Header
                data-testid="cart-order-summary-title"
                as="h4"
                variant="header4"
                textTransform="uppercase"
                color="slt-pepper.1000"
            >
                <FormattedMessage id="order_summary" defaultMessage="Order Summary" />
            </Header>
            <BasketSummary isFromCart />
            {showShippingProtection && (
                <ShippingProtectionToggle
                    shippingProtectionProduct={shippingProtectionProduct}
                    isLoading={isShippingProtectionLoading}
                    pageSource={PageSource.CART}
                />
            )}
            <Flex direction="column" gap={4}>
                <AfterpaySection
                    eligible={paymentMethods?.afterPay.eligible}
                    afterpayConfig={paymentMethods?.afterPay.config}
                    mode="messaging"
                />

                <Flex direction="column" gap={4}>
                    <CartButtons id="cart-order-summary-buttons" />

                    <TermsConditionsPrivacyPolicy isFromCart />

                    <ClientOnly>
                        <CartPerks shippingProtectionBonus={hasShippingProtectionInCart} />
                    </ClientOnly>
                </Flex>
            </Flex>
        </Flex>
    );
};
